import type { AgentRun, TraceEvent } from '../types';

const record = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const text = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const optionalText = (value: unknown) => value == null || typeof value === 'string';

// These endpoints also return historical safety-event rows as synthetic runs.
// Keep that documented fallback while checking every field the timeline renders.
export type TraceEvidence = { run: AgentRun; events: TraceEvent[]; historical: boolean };
export function parseTraceEvidence(value: unknown, id: string): TraceEvidence {
  if (!record(value) || !record(value.run) || value.run.id !== id || !text(value.run.agent_id) || !Array.isArray(value.events)) throw Error('Invalid trace evidence.');
  const run = value.run;
  const historical = run.started_at == null;
  const date = historical ? run.timestamp : run.started_at;
  if (!text(date) || !Number.isFinite(Date.parse(date))) throw Error('Invalid trace evidence.');
  for (const field of ['task', 'action_name', 'agent_name', 'trace_id']) if (!optionalText(run[field])) throw Error('Invalid trace evidence.');
  const seen = new Set<string>();
  for (const event of value.events) {
    if (!record(event) || !text(event.id) || seen.has(event.id) || event.agent_id !== run.agent_id ||
      (event.run_id != null && event.run_id !== id && event.id !== id) || !text(event.event_type) ||
      !text(event.timestamp) || !Number.isFinite(Date.parse(event.timestamp))) throw Error('Invalid trace evidence.');
    for (const field of ['action_name', 'tool_name', 'payload_summary', 'outcome']) if (!optionalText(event[field])) throw Error('Invalid trace evidence.');
    if (event.decision != null && !['ALLOW', 'REVIEW', 'BLOCK'].includes(String(event.decision))) throw Error('Invalid trace evidence.');
    seen.add(event.id);
  }
  return { ...(value as { run: AgentRun; events: TraceEvent[] }), historical };
}

export function parseTraceExport(value: unknown, id: string, agentId?: string): Record<string, unknown> {
  if (!record(value) || value.run_id !== id || (agentId !== undefined && value.agent_id !== agentId) || !text(value.integrity_hash) || !/^[a-f0-9]{64}$/i.test(value.integrity_hash) ||
    !text(value.exported_at) || !Number.isFinite(Date.parse(value.exported_at))) throw Error('Invalid trace export.');
  for (const field of ['trace_events', 'safety_events', 'incidents']) if (!Array.isArray(value[field])) throw Error('Invalid trace export.');
  for (const field of ['trace_events', 'safety_events']) for (const event of value[field] as unknown[]) {
    if (!record(event) || event.run_id !== id) throw Error('Invalid trace export scope.');
  }
  return value;
}
